import { HttpClient, HttpResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { LOCATION } from "@tylertech/shared-module";
import { TylerIdModel, TylerIdSettings } from "@tylertech/tyler-api-types";
import * as oidcClient from "oidc-client-ts";
import { lastValueFrom, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { SecurityRoute, SecurityStorageKey, SecurityUrls } from "./security.service";
import { StorageService } from "./storageService";
import { RouteInfoService } from "./routeInfo.service";

export interface UserInfo
{
    user?: oidcClient.User;
    isAuthenticated?: boolean;
}

export const enum OpenIdStorageKey
{
    CachedTidInfo = "cachedTidInfo",
}

export interface StoredTylerIdSettings
    extends TylerIdSettings
{
    $timestamp?: number;
}

@Injectable()
export class OpenIdService
{
    private readonly log = getLoggerFor("OpenIdService");
    private _userInfo: UserInfo;
    private initializationPromise: Promise<oidcClient.UserManager>;

    constructor(
        @Inject(LOCATION) private readonly location: Location,
        private readonly $http: HttpClient,
        private readonly router: Router,
        private readonly $storageService: StorageService,
        private $routeInfoService: RouteInfoService
    )
    {
        oidcClient.Log.setLogger(console);
        oidcClient.Log.setLevel(oidcClient.Log.INFO);

        this._userInfo = {};
    }

    private get TylerIdSettings(): TylerIdSettings
    {
        return this.cachedTidInfo;
    }

    private get cachedTidInfo(): StoredTylerIdSettings
    {
        return this.$storageService.Get<StoredTylerIdSettings>(OpenIdStorageKey.CachedTidInfo);
    }

    private set cachedTidInfo(value: StoredTylerIdSettings)
    {
        value.$timestamp = Date.now();
        this.$storageService.Set(OpenIdStorageKey.CachedTidInfo, value);
    }

    private get userInfo(): UserInfo
    {
        return this._userInfo;
    }

    private HasCachedInfo(): boolean
    {
        if (!this.cachedTidInfo)
        {
            return false;
        }

        const timestamp = new Date(this.cachedTidInfo.$timestamp);
        timestamp.setHours(timestamp.getHours() + 24);
        if (timestamp < new Date())
        {
            this.$storageService.Remove(OpenIdStorageKey.CachedTidInfo);
            return false;
        }

        return true;
    }

    clearToken(): void
    {
        Object.keys(localStorage)
            .filter(x => x.includes("oidc."))
            .forEach(key =>
            {
                localStorage.removeItem(key);
            });

        Object.keys(sessionStorage)
            .filter(x => x.includes("oidc."))
            .forEach(key =>
            {
                sessionStorage.removeItem(key);
            });
    }

    private createUserManager(tidSettings: TylerIdSettings): oidcClient.UserManager
    {
        if (!tidSettings)
        {
            throw Error("NgOidcUserService: Must set Settings with the required options.");
        }
        const settings: oidcClient.UserManagerSettings = {
            accessTokenExpiringNotificationTimeInSeconds: tidSettings.accessTokenExpiringNotificationTimeInSeconds,
            authority: tidSettings.authority,
            automaticSilentRenew: tidSettings.automaticSilentRenew,
            client_id: tidSettings.client_id,
            post_logout_redirect_uri: tidSettings.post_logout_redirect_uri,
            redirect_uri: tidSettings.redirect_uri,
            scope: tidSettings.scope,
            response_mode: "query",
            response_type: "code",
            extraQueryParams: {
                crmId: tidSettings.crmId,
            },
            popup_redirect_uri: `${this.location.origin}${this.$routeInfoService.baseUrl}/${SecurityRoute.PopupLoginCallback}`,
        };

        const manager = new oidcClient.UserManager(settings);
        manager.getUser()
            .then(user =>
            {
                if (user)
                {
                    this._userInfo.user = user;
                    this._userInfo.isAuthenticated = true;
                }
            });
        manager.events.addUserLoaded(u =>
        {
            this.userInfo.user = u;
            this.userInfo.isAuthenticated = true;
            this.log.information("user loaded");
        });
        manager.events.addAccessTokenExpiring(() =>
        {
            this.log.information("token expiring");
        });
        manager.events.addAccessTokenExpired(() =>
        {
            this.userInfo.user = null;
            this.userInfo.isAuthenticated = false;
            this.log.warning("token expired");
        });
        manager.events.addSilentRenewError(e =>
        {
            this.userInfo.user = null;
            this.userInfo.isAuthenticated = false;
            this.log.warning("silent renew error", e.message);
        });
        return manager;
    }

    private getCachedTylerIdSettings(): Promise<TylerIdSettings>
    {
        if (this.HasCachedInfo())
        {
            return Promise.resolve(this.TylerIdSettings);
        }
        return this.getTylerIdSettings();
    }

    getToken(): string
    {
        return this.userInfo.user?.id_token;
    }

    private getTylerIdSettings(): Promise<TylerIdSettings>
    {
        const tylerIdModel: TylerIdModel = {
            Token: null,
            Tenant: this.$routeInfoService.tenant,
        };
        const request = this.$http.post<TylerIdSettings>(SecurityUrls.TylerIdSettings,
            tylerIdModel,
            {
                observe: "response",
                responseType: 'json',
            });

        return lastValueFrom(
            request
                .pipe(
                    map((response: HttpResponse<TylerIdSettings>) =>
                    {
                        // TODO - should we be testing the response's HttpStatusCode before just assuming everything is fine?
                        const data = response.body;
                        this.cachedTidInfo = data;

                        return this.TylerIdSettings;
                    }),
                    catchError((error) =>
                    {
                        this.log.error("Error fetching TylerIdSettings");
                        this.log.error(error);

                        return throwError(() => new Error(`Error fetching TylerIdSettings: ${error}`));
                    })));
    }

    getUser(): Promise<oidcClient.User | void>
    {
        return this.getUserManager()
            .then(userManager =>
            {
                return userManager.getUser()
                    .then(user =>
                    {
                        if (!user)
                        {
                            return userManager.signinCallback();
                        }

                        this.userInfo.user = user;
                        this.userInfo.isAuthenticated = true;
                        return user;
                    });
            });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private getUserManager(): Promise<oidcClient.UserManager>
    {
        if (this.initializationPromise)
        {
            return this.initializationPromise;
        }

        this.initializationPromise = this.getCachedTylerIdSettings()
            .then((settings: TylerIdSettings & oidcClient.UserManagerSettings) =>
            {
                if (!settings)
                {
                    return null;
                }
                return this.createUserManager(settings);
            })
            .finally(() =>
            {
                this.initializationPromise = undefined;
            });

        return this.initializationPromise;
    }

    @LogInvocation(LogLevel.DEBUG)
    populateToken(): Promise<any>
    {
        const token = this.getToken();
        if (token)
            return Promise.resolve();
        return this.getUserManager();
    }

    @LogInvocation(LogLevel.DEBUG)
    login(redirectUri?: string): Promise<void>
    {
        return this.signInRedirect(redirectUri);
    }

    @LogInvocation(LogLevel.DEBUG)
    loginCallBack()
    {
        return this.getUserManager()
            .then(userManager =>
            {
                return userManager.signinCallback();
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    logout(): Promise<void>
    {
        return this.signOutRedirect();
    }

    private signInRedirect(redirectUri?: string): Promise<void>
    {
        const args: oidcClient.SigninRedirectArgs = {
            redirect_uri: redirectUri ?? this.location.href,
            // silent_redirect_uri: this.location.href,
        };
        return this.getUserManager()
            .then(x =>
            {
                x.signinRedirect(args);
            });
    }

    loginPopup(): Promise<oidcClient.User>
    {
        return this.signInPopup();
    }

    @LogInvocation(LogLevel.DEBUG)
    private signInPopup(): Promise<oidcClient.User>
    {
        const args: oidcClient.SigninPopupArgs = {
            // redirect_uri:  this.location.href,
        };
        return this.getUserManager()
            .then(x =>
            {
                return x.signinPopup(args);
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    loginPopupCallBack(): Promise<oidcClient.User | void>
    {
        return this.getUserManager()
            .then(userManager =>
            {
                return userManager.signinCallback();
            });
    }

    private signOutRedirect(): Promise<void>
    {
        const tidToken = this.$storageService.Get<string>(SecurityStorageKey.TidToken);
        if (!tidToken)
        {
            this.router.navigate([SecurityRoute.Signout]);
            return;
        }

        const url = `${this.$routeInfoService.domain}${this.$routeInfoService.baseUrl}/${SecurityRoute.Signout}`;
        const args: oidcClient.SignoutRedirectArgs = {
            id_token_hint: tidToken,
            post_logout_redirect_uri: url,
        };

        return this.getUserManager()
            .then(
                userManager =>
                {
                    return userManager.signoutRedirect(args)
                        .catch(_e =>
                        {
                            this.log.error(_e);
                        });
                },
                e =>
                {
                    this.log.error(e);
                });
    }
}
